/* eslint-disable camelcase */
import { defineComponent } from 'vue';
// import Card, { CardProps } from '../../Card/Card'
import stringContains_inLowerCase from '../../../utils/stringOperations/stringContains_inLowerCase';
import CodeRenderer from '../../CodeRenderer/CodeRenderer.vue';
import arrayLength from 'src/utils/arrayOperations/arrayLength';
const tabs = [
    { name: 'props', label: 'Props' },
    { name: 'data', label: 'Data' },
    { name: 'computed', label: 'Computed' },
    { name: 'methods', label: 'Methods' }
];
const types = ['props', 'state', 'computed', 'methods'];
export default defineComponent({
    name: 'GenericTableInternalsViewer',
    components: { CodeRenderer },
    props: {
        ctx: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            isMinimized: true,
            selectedTab: 'data',
            searchTermToFilterKeys: '',
            hidePropertiesOfRenderingFrameworks: true,
            hideKeysWithEmptyValues: true,
            hideFunctions: true
        };
    },
    computed: {
        tabs() {
            return [...tabs];
        },
        cardProps() {
            return {
                hideFooter: true
            };
        },
        accessorKeysToDisplay() {
            const ctx = this.ctx;
            const composeKey = (name, type) => ({ name, type, jsType: ctx.getJsTypeOfKey(name) });
            let keyObjects = [
                ...ctx.getPropsKeys().map(name => composeKey(name, 'props')),
                ...ctx.getStateKeys().map(name => composeKey(name, 'state')),
                ...ctx.getComputedKeys().map(name => composeKey(name, 'computed')),
                ...ctx.getMethodsKeys().map(name => composeKey(name, 'methods'))
            ];
            if (this.searchTermToFilterKeys) {
                keyObjects = keyObjects.filter(key => stringContains_inLowerCase(String(key.name), this.searchTermToFilterKeys));
            }
            if (this.hidePropertiesOfRenderingFrameworks) {
                // _ and $ are used by Vue and Quasar
                keyObjects = keyObjects.filter(key => !String(key).startsWith('_') && !String(key).startsWith('$'));
            }
            if (this.hideKeysWithEmptyValues) {
                keyObjects = keyObjects.filter(key => !this.valueOfKeyIsEmpty(key));
            }
            if (this.hideFunctions) {
                keyObjects = keyObjects.filter(key => typeof ctx[key.name] !== 'function');
            }
            // sort by disabled last, then by name of key
            keyObjects = keyObjects.sort((a, b) => {
                if (this.valueOfKeyIsEmpty(a) && !this.valueOfKeyIsEmpty(b))
                    return 1;
                if (!this.valueOfKeyIsEmpty(a) && this.valueOfKeyIsEmpty(b))
                    return -1;
                return String(a.name).localeCompare(String(b.name));
            });
            return keyObjects;
        },
    },
    methods: {
        getExpansionItemProps(keyObj) {
            let label = keyObj.name + ' (' + keyObj.jsType + ')';
            if (keyObj.jsType === 'array')
                label += ' Length: ' + arrayLength(this.ctx[keyObj.name]);
            if (keyObj.jsType === 'number')
                label += ' Value: ' + this.ctx[keyObj.name];
            if (keyObj.jsType === 'boolean')
                label += ' Value: ' + this.ctx[keyObj.name];
            if (keyObj.jsType === 'string')
                label += ' Value: ' + this.ctx[keyObj.name];
            let hideExpandIcon = false;
            if (['boolean', 'function', 'number', 'string'].includes(keyObj.jsType))
                hideExpandIcon = true;
            if (keyObj.jsType === 'array' && arrayLength(this.ctx[keyObj.name]) === 0)
                hideExpandIcon = true;
            return {
                hideExpandIcon,
                // label: keyObj.name + ' (' + this.ctx.getJsTypeOfKey(keyObj.name) + ')',
                label,
                headerClass: 'monospace',
                disable: this.valueOfKeyIsEmpty(keyObj),
                icon: this.getIcon(keyObj),
                expandIcon: 'mdi-chevron-down',
                expandedIcon: 'mdi-chevron-up',
                popup: true
            };
        },
        getIcon(keyObj) {
            const key = keyObj.name;
            const typeOfKey = this.ctx.getJsTypeOfKey(key);
            const value = () => { var _a, _b; return (_b = (_a = this.ctx.getStateByKey(key)) !== null && _a !== void 0 ? _a : this.ctx.getComputedByKey(key)) !== null && _b !== void 0 ? _b : this.ctx.getPropByKey(key); };
            switch (typeOfKey) {
                case 'function':
                    return 'mdi-function-variant';
                case 'array':
                    return 'mdi-code-brackets';
                case 'object':
                    return 'mdi-code-json';
                case 'string':
                    return 'mdi-alphabetical-variant';
                case 'number':
                    return 'mdi-numeric';
                case 'boolean':
                    return value() ? 'mdi-checkbox-outline' : 'mdi-checkbox-blank-outline';
                default:
                    return 'mdi-help-circle-outline';
            }
        },
        getJsTypeOfKey(key) {
            return this.ctx.getJsTypeOfKey(key);
        },
        valueOfKeyIsEmpty(key) {
            return this.ctx.getStateByKey(key.name) === undefined &&
                this.ctx.getComputedByKey(key.name) === undefined &&
                this.ctx.getPropByKey(key.name) === undefined;
        },
        getCodeRendererProps(key) {
            const getJsonContentOfKey = (key) => {
                var _a, _b;
                if (key.jsType === 'function')
                    return 'Is function';
                const value = (_b = (_a = this.ctx.getPropByKey(key.name)) !== null && _a !== void 0 ? _a : this.ctx.getStateByKey(key.name)) !== null && _b !== void 0 ? _b : this.ctx.getComputedByKey(key.name);
                if (value === undefined)
                    return 'Empty';
                return JSON.stringify(value, null, 2);
            };
            try {
                return {
                    code: key.type === 'methods'
                        ? 'Is function'
                        : getJsonContentOfKey(key),
                    codeLanguage: 'JSON',
                    callBackForSaving: (newVal) => {
                        try {
                            if (key.type === 'state') {
                                this.ctx.setStateByKey(key.name, JSON.parse(newVal));
                            }
                            else if (key.type === 'computed') {
                                console.error('Computed properties are read-only');
                            }
                            else if (key.type === 'methods') {
                                console.error('Methods are read-only');
                            }
                        }
                        catch (error) {
                            console.error('Error in getCodeRendererProps1', error);
                        }
                    }
                };
            }
            catch (error) {
                return {
                    code: 'Error in getCodeRendererProps3: \n' + error,
                    codeLanguage: 'plaintext',
                };
            }
        }
    }
});
